// API Service for Cloudflare Worker backend
// Handles communication between frontend and Worker API
//
// Auth model: the front end exchanges the PIN for an API token via /api/auth
// (see authApi below). The token is stored in sessionStorage (cleared on tab
// close) and attached as `Authorization: Bearer <token>` to every request.

const WORKER_URL =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) ||
  'https://clystcore-api.chris-c6d.workers.dev'

const API_BASE = `${WORKER_URL}/api`

const TOKEN_STORAGE_KEY = 'clystcore_api_token'
const REQUEST_TIMEOUT_MS = 30_000

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------

export function getApiToken() {
  try {
    return sessionStorage.getItem(TOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

export function setApiToken(token) {
  try {
    if (token) sessionStorage.setItem(TOKEN_STORAGE_KEY, token)
    else sessionStorage.removeItem(TOKEN_STORAGE_KEY)
  } catch {
    // ignore — private mode etc.
  }
}

export function clearApiToken() {
  setApiToken(null)
}

// ---------------------------------------------------------------------------
// Core request helper
// ---------------------------------------------------------------------------

async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`

  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {})
  }

  const token = getApiToken()
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  let response
  try {
    response = await fetch(url, { ...options, headers, signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }

  if (response.status === 401) {
    clearApiToken()
    // Force the user back to the PIN screen on the next render
    try { sessionStorage.removeItem('app_unlocked') } catch {}
    throw new Error('Session expired. Please re-enter your PIN.')
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || `API error: ${response.status}`)
  }

  return response.json()
}

// ---------------------------------------------------------------------------
// Auth API
// ---------------------------------------------------------------------------

export const authApi = {
  // Exchange a PIN for an API token. On success, stores the token.
  async loginWithPin(pin) {
    const res = await fetch(`${API_BASE}/auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.error || 'Authentication failed')
    }
    const { token } = await res.json()
    setApiToken(token)
    return token
  },

  logout() {
    clearApiToken()
  }
}

// ---------------------------------------------------------------------------
// Survey API
// ---------------------------------------------------------------------------

export const surveyApi = {
  async list() {
    const { surveys } = await apiRequest('/surveys')
    return surveys
  },

  async get(id) {
    const data = await apiRequest(`/surveys/${id}`)
    return data
  },

  async create(survey) {
    const { survey: created } = await apiRequest('/surveys', {
      method: 'POST',
      body: JSON.stringify(survey)
    })
    return created
  },

  async update(id, updates) {
    const { survey: updated } = await apiRequest(`/surveys/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    })
    return updated
  },

  async delete(id) {
    await apiRequest(`/surveys/${id}`, { method: 'DELETE' })
  },

  async analyse(id) {
    const data = await apiRequest(`/surveys/${id}/analyse`, { method: 'POST' })
    return data
  },

  async analyseCategory(id, categoryCode) {
    const data = await apiRequest(`/surveys/${id}/analyse/${categoryCode}`, { method: 'POST' })
    return data
  },

  async generate(id) {
    const data = await apiRequest(`/surveys/${id}/generate`, { method: 'POST' })
    return data
  },

  async researchProperty(id) {
    const data = await apiRequest(`/surveys/${id}/research-property`, { method: 'POST' })
    return data
  },

  async generateSectionC(id) {
    const data = await apiRequest(`/surveys/${id}/generate-section-c`, { method: 'POST' })
    return data
  }
}

// ---------------------------------------------------------------------------
// Photo API
// ---------------------------------------------------------------------------

export const photoApi = {
  async upload(surveyId, categoryCode, imageBlob, thumbnailBlob = null) {
    // Step 1: Get upload keys from the Worker
    const { photoId, r2Key, thumbnailR2Key } = await apiRequest('/photos/upload', {
      method: 'POST',
      body: JSON.stringify({ surveyId, categoryCode })
    })

    // Step 2: Encode payload
    const imageBase64 = await blobToBase64(imageBlob)
    const thumbnailBase64 = thumbnailBlob ? await blobToBase64(thumbnailBlob) : null

    // Step 3: Confirm upload with image data
    const { photo } = await apiRequest('/photos/confirm', {
      method: 'POST',
      body: JSON.stringify({
        photoId,
        surveyId,
        categoryCode,
        r2Key,
        thumbnailR2Key,
        imageData: imageBase64,
        thumbnailData: thumbnailBase64,
        analyseWithAI: true
      })
    })

    return photo
  },

  async update(photoId, updates) {
    const { photo } = await apiRequest(`/photos/${photoId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    })
    return photo
  },

  async delete(photoId) {
    await apiRequest(`/photos/${photoId}`, { method: 'DELETE' })
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export async function convertToWebP(blob, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)
      canvas.toBlob(
        (webpBlob) => webpBlob ? resolve(webpBlob) : reject(new Error('Failed to convert to WebP')),
        'image/webp',
        quality
      )
    }
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = URL.createObjectURL(blob)
  })
}

export async function convertToJPEG(blob, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0)
      canvas.toBlob(
        (jpegBlob) => jpegBlob ? resolve(jpegBlob) : reject(new Error('Failed to convert to JPEG')),
        'image/jpeg',
        quality
      )
    }
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = URL.createObjectURL(blob)
  })
}

// ---------------------------------------------------------------------------
// Prompts API
// ---------------------------------------------------------------------------

export const promptsApi = {
  async getAll() {
    const { prompts } = await apiRequest("/prompts")
    return prompts
  },

  async get(categoryCode) {
    const prompt = await apiRequest(`/prompts/${categoryCode}`)
    return prompt
  },

  async save(categoryCode, prompt) {
    const result = await apiRequest(`/prompts/${categoryCode}`, {
      method: "PUT",
      body: JSON.stringify({ prompt })
    })
    return result
  },

  async reset(categoryCode) {
    const result = await apiRequest(`/prompts/${categoryCode}`, { method: "DELETE" })
    return result
  }
}

// ---------------------------------------------------------------------------
// Custom Categories API
// ---------------------------------------------------------------------------

export const customCategoriesApi = {
  async getAll() {
    const { categories } = await apiRequest("/custom-categories")
    return categories
  },

  async create(categoryData) {
    const { category } = await apiRequest("/custom-categories", {
      method: "POST",
      body: JSON.stringify(categoryData)
    })
    return category
  },

  async update(id, updates) {
    const { category } = await apiRequest(`/custom-categories/${id}`, {
      method: "PUT",
      body: JSON.stringify(updates)
    })
    return category
  },

  async delete(id) {
    await apiRequest(`/custom-categories/${id}`, { method: "DELETE" })
  }
}

// ---------------------------------------------------------------------------
// Health check
// ---------------------------------------------------------------------------

export async function checkApiHealth() {
  try {
    const data = await apiRequest("/health")
    return { ok: true, ...data }
  } catch (error) {
    return { ok: false, error: error.message }
  }
}
